/* eslint-disable no-nested-ternary */
/* eslint-disable react-hooks/exhaustive-deps */
import { useMedia } from 'domains/commons/hooks/use-media';
import React, { MouseEventHandler, useContext, useEffect, useState } from 'react';
import Cleave from 'cleave.js/react';
import './text-box.scss';
import { DataLayer } from 'libs/providers/datalayer/datalayer';
import {
  globalFlow,
  VIRTUAL_EVENT_ACTIONS,
  VIRTUAL_EVENT_CATEGORIES,
  VIRTUAL_EVENT_SECTION,
  VIRTUAL_EVENT_SUB_ZONE,
  VIRTUAL_EVENT_TYPE_ELEMENT,
  VIRTUAL_EVENT_TYPE_EVENT,
  VIRTUAL_EVENT_TYPE_ZONE,
  VIRTUAL_EVENT_ZONE,
} from 'libs/providers/datalayer/constants';
import { AppContext } from 'domains/commons/contexts/global/global-provider';

interface InputValidator {
  exp: 'required' | 'min' | 'max' | RegExp;
  msg: string;
  value?: number;
}

type Props = {
  label?: string;
  id?: string;
  placeholder?: string;
  type?: string;
  value?: string;
  onChange?: any;
  onFocus?: any;
  onKeyUp?: any;
  onBlur?: any;
  onClick?: MouseEventHandler<HTMLInputElement>;
  classname?: string;
  telSymbol?: boolean;
  moneySymbol?: boolean;
  disabled?: boolean;
  abbr?: string;
  extraInfo?: any;
  rightIcon?: any;
  validators?: InputValidator[];
  onValidState?: any;
  cleaveOptions?: any;
  name?: string;
  maxLength?: number;
  dataLayer?: boolean;
  messageDataLayer?: string;
  readonly?: boolean;
};

const TextBox: React.FC<Props> = (props: Props) => {
  const {
    label,
    id,
    placeholder,
    type,
    value = '',
    onChange,
    classname,
    telSymbol,
    moneySymbol,
    abbr,
    extraInfo,
    rightIcon,
    onFocus,
    onBlur,
    validators,
    onValidState,
    cleaveOptions,
    name,
    disabled,
    maxLength,
    onKeyUp,
    dataLayer,
    messageDataLayer,
    onClick = () => { },
    readonly = false
  } = props;
  const { store } = useContext(AppContext);
  const { isMobile } = useMedia();
  const [placeholderValue, setPlaceholder] = useState(placeholder);
  const lengthPlaceholder = placeholderValue?.length ?? 0;
  const initPaddingLeft =
    lengthPlaceholder <= 2
      ? lengthPlaceholder * (isMobile ? 10 : 15)
      : lengthPlaceholder * (isMobile ? 8 : 13);
  const [paddingLeft, setPaddingLeft] = useState(initPaddingLeft);
  const handleFocus = () => setPlaceholder(placeholder);
  const classTelSymbol = telSymbol || moneySymbol ? 'pl' : '';

  /*
   * validators
   */
  const [touched, setTouched] = useState(false);
  const validatorMsg = touched
    ? validators?.find((item: InputValidator) => {
      if (item.exp === 'required' && !value) {
        return item;
      }

      if (item.exp === 'min' && +value < (item.value ?? 0)) {
        return item;
      }

      if (item.exp === 'max' && +value > (item.value ?? 0)) {
        return item;
      }

      if (item.exp instanceof RegExp && !item.exp.test(value)) {
        return item;
      }

      return '';
    })?.msg
    : '';

  useEffect(() => {
    if (value) {
      setTouched(true);
    }
  }, []);

  useEffect(() => {
    if (!validators?.length) return;

    onValidState && onValidState(!touched ? false : !validatorMsg);
  }, [value, touched]);
  /*
   * end validators
   */

  useEffect(() => {
    const valuePaddingLeft =
      abbr !== 'm' ? (isMobile ? 10 : 15) : isMobile ? 8 : 13;
    value?.length > 1 && setPaddingLeft(value.length * valuePaddingLeft);
  }, [value]);

  useEffect(() => {
    if (dataLayer) {
      if (validatorMsg) {
        DataLayer.setVirtualEvent({
          category: VIRTUAL_EVENT_CATEGORIES.default,
          action: VIRTUAL_EVENT_ACTIONS.cotizacion,
          label: label,
          tipoZona: VIRTUAL_EVENT_TYPE_ZONE.autos,
          zona: VIRTUAL_EVENT_ZONE.nuevo,
          subZona: VIRTUAL_EVENT_SUB_ZONE.paso1,
          sección: VIRTUAL_EVENT_SECTION.modal,
          tipoEvento: VIRTUAL_EVENT_TYPE_EVENT.visualizar,
          tipoElemento: VIRTUAL_EVENT_TYPE_ELEMENT.modalMessageError,
          valor: `${messageDataLayer}:${validatorMsg}`,
          flujo: globalFlow(store.dataLayer),
        });
      }
    }
  }, [validatorMsg]);

  return (
    <div
      id={`ui-input__${id}`}
      className={`ui-input ${classname} ${validatorMsg ? 'ui-input--invalid' : ''
        } ${disabled ? 'disabled' : ''}`}
      data-testid="text-box"
    >
      <label htmlFor={id} className="ui-label">
        {label}
      </label>
      <div className="ui-form">
        <>
          {abbr ? (
            <span
              className="abbr-right"
              style={{ paddingLeft: `${paddingLeft}px` }}
            >
              {abbr}
            </span>
          ) : null}
          {telSymbol && <span className="symbol-tel">+51</span>}
          {moneySymbol && <span className="symbol-tel">US$</span>}

          {!cleaveOptions ? (
            <input
              disabled={disabled}
              data-testid="input"
              maxLength={maxLength}
              type={type}
              id={id}
              className={classTelSymbol}
              placeholder={placeholderValue}
              value={value}
              onChange={onChange}
              autoComplete="off"
              onFocus={(e) => {
                !touched && setTouched(true);
                if (onFocus) {
                  onFocus(e);
                } else {
                  handleFocus();
                }
              }}
              onBlur={(e) => onBlur(e)}
              onKeyUp={onKeyUp}
              readOnly={readonly}
              onClick={onClick}
            />
          ) : (
            <Cleave
              {...cleaveOptions}
              disabled={disabled}
              value={value}
              id={id}
              name={name}
              onChange={onChange}
              type={type}
              placeholder={placeholderValue}
              autoComplete="off"
              className={telSymbol ? 'pl' : ''}
              onFocus={(e) => {
                !touched && setTouched(true);
                if (onFocus) {
                  onFocus(e);
                } else {
                  handleFocus();
                }
              }}
              onBlur={(e) => onBlur(e)}
            />
          )}
        </>
        {rightIcon || null}
      </div>
      <span className="ui-input__extra-info">{extraInfo}</span>
      {!!validators?.length && (
        <p className="ui-input__invalid_msg">{validatorMsg}</p>
      )}
    </div>
  );
};

TextBox.defaultProps = {
  label: '',
  id: '',
  placeholder: '',
  type: 'text',
  value: '',
  onChange: () => { },
  onBlur: () => { },
  onFocus: null,
  classname: '',
  telSymbol: false,
  moneySymbol: false,
  disabled: false,
  abbr: '',
  extraInfo: null,
  rightIcon: null,
  validators: [],
  onValidState: null,
  cleaveOptions: null,
  name: '',
};

export default TextBox;
